/**
 * Analyze OCR Actor
 *
 * Unified actor for OCR processing that handles both:
 * - Government-issued ID cards (driver's license, state ID, passport) using AnalyzeID
 * - Insurance cards (or any document) using AnalyzeDocument with custom queries
 *
 * The actor mode and context key are determined by input parameters,
 * making it reusable for ocrIdData, ocrPrimaryInsuranceData, ocrSecondaryInsuranceData
 */

import { datadogLogs } from "@datadog/browser-logs";
import { fromPromise } from "xstate";
import { z } from "zod";
import {
  type ParsedIdData,
  analyzeDocumentImage,
  analyzeIdImage,
  calculateConfidence,
  logOcrUsage,
  meetsConfidenceThreshold,
  parseAnalyzeIdResponse,
  parseQueryResults,
  parseQueryResultsWithBoundingBoxes,
} from "../../lib/ocr";
import type { ActorWithMetadata } from "./send-sms.actor";

/**
 * Input Schema
 */
const AnalyzeOcrInputSchema = z.object({
  // Mode determines which Textract command to use
  mode: z.enum(["id", "insurance"]),

  // Image data (base64 encoded)
  imageBase64: z.string(),

  // For insurance mode: custom queries to ask
  queries: z.array(z.string()).optional(),

  // Context key prefix for saving results (e.g., "ocrData", "ocrPrimaryInsurance", "ocrSecondaryInsurance")
  contextKey: z.enum([
    "ocrData",
    "ocrPrimaryInsurance",
    "ocrSecondaryInsurance",
  ]),

  // Optional context for logging
  consumerId: z.string().optional(),
  orgId: z.number().optional(),

  // Confidence threshold (default 0.85 for ID, 0.80 for insurance)
  confidenceThreshold: z.number().min(0).max(1).optional(),
});

type AnalyzeOcrInput = z.infer<typeof AnalyzeOcrInputSchema>;

/**
 * Success Schema
 */
const AnalyzeOcrSuccessSchema = z.record(z.unknown()); // Generic data object - structure depends on mode

type AnalyzeOcrSuccess = z.infer<typeof AnalyzeOcrSuccessSchema>;

/**
 * Error Schema
 */
const BoundingBoxSchema = z.object({
  Top: z.number(),
  Left: z.number(),
  Width: z.number(),
  Height: z.number(),
});

const AnalyzeOcrErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "MISSING_IMAGE",
    "MISSING_QUERIES",
    "OCR_API_ERROR",
    "LOW_CONFIDENCE",
    "NETWORK_ERROR",
    "PARSE_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
  confidence: z.number().optional(),
  mode: z.enum(["id", "insurance"]),
  contextKey: z.string(),
  boundingBoxes: z.record(BoundingBoxSchema.nullable()).optional(),
  queryResults: z.record(z.string().nullable()).optional(),
});

type AnalyzeOcrError = z.infer<typeof AnalyzeOcrErrorSchema>;

/**
 * Default queries for insurance cards
 */
const DEFAULT_INSURANCE_QUERIES = [
  "What is the insurance company or payer name?",
  "What is the payer name?",
  "What is the member ID or subscriber ID?",
  "What is the member ID?",
  "What is the group number?",
  "What is the RxBIN number?",
  "What is the RxPCN number?",
  "What is the RxGRP?",
  "What is the plan name?",
  "What is the plan type?",
  "What is the subscriber or patient name?",
  "What is the subscriber name?",
  "What is the effective date?",
  "What is the expiration date?",
];

/**
 * Default queries for extracting sex/gender from ID cards
 */
const ID_GENDER_QUERIES = [
  "What is the person's gender?",
  "What is the person's sex?",
];

/**
 * Actor Implementation
 */
const analyzeOcrActorImpl = fromPromise(
  async ({ input }: { input: AnalyzeOcrInput }): Promise<AnalyzeOcrSuccess> => {
    // Validate input
    const validated = AnalyzeOcrInputSchema.safeParse(input);

    if (!validated.success) {
      const errorData: AnalyzeOcrError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
        mode: input.mode || "id",
        contextKey: input.contextKey || "ocrId",
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    const {
      mode,
      imageBase64,
      queries,
      contextKey,
      consumerId,
      orgId,
      confidenceThreshold,
    } = validated.data;

    // Check if we have image data
    if (!imageBase64) {
      const errorData: AnalyzeOcrError = {
        code: "MISSING_IMAGE",
        message: "Image is required for OCR analysis",
        retryable: false,
        mode,
        contextKey,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    // Set default confidence threshold based on mode
    const threshold = confidenceThreshold || (mode === "id" ? 0.7 : 0.6);

    try {
      if (mode === "id") {
        // ID MODE: Use AnalyzeID + separate gender query
        return await processIdOcr(
          imageBase64,
          threshold,
          contextKey,
          consumerId,
          orgId,
        );
      }
      // INSURANCE MODE: Use AnalyzeDocument with queries
      const insuranceQueries = queries || DEFAULT_INSURANCE_QUERIES;

      if (insuranceQueries.length === 0) {
        const errorData: AnalyzeOcrError = {
          code: "MISSING_QUERIES",
          message: "Queries are required for insurance OCR analysis",
          retryable: false,
          mode,
          contextKey,
        };
        const error = new Error(errorData.message);
        Object.assign(error, errorData);
        throw error;
      }

      return await processInsuranceOcr(
        imageBase64,
        insuranceQueries,
        threshold,
        contextKey,
        consumerId,
        orgId,
      );
    } catch (error) {
      // Re-throw typed errors
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        "message" in error
      ) {
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes("fetch")) {
        const errorData: AnalyzeOcrError = {
          code: "NETWORK_ERROR",
          message: "Network error while calling OCR API",
          retryable: true,
          mode,
          contextKey,
        };
        const err = new Error(errorData.message);
        Object.assign(err, errorData);
        throw err;
      }

      // Wrap unknown errors
      const errorData: AnalyzeOcrError = {
        code: "PARSE_ERROR",
        message: error instanceof Error ? error.message : "Unknown OCR error",
        retryable: true,
        mode,
        contextKey,
      };
      const err = new Error(errorData.message);
      Object.assign(err, errorData);
      throw err;
    }
  },
);

/**
 * Process ID card OCR
 */
async function processIdOcr(
  imageBase64: string,
  threshold: number,
  contextKey: string,
  consumerId?: string,
  orgId?: number,
): Promise<AnalyzeOcrSuccess> {
  // Call AnalyzeID endpoint
  const idResult = await analyzeIdImage(imageBase64);

  if (!idResult.response) {
    const errorData: AnalyzeOcrError = {
      code: "OCR_API_ERROR",
      message: "AnalyzeID failed",
      retryable: true,
      mode: "id",
      contextKey,
    };
    const error = new Error(errorData.message);
    Object.assign(error, errorData);
    throw error;
  }

  // Parse ID data
  const idData: ParsedIdData = parseAnalyzeIdResponse(idResult.response);

  // Call AnalyzeDocument for gender (separate call)
  const genderResult = await analyzeDocumentImage(
    imageBase64,
    ID_GENDER_QUERIES,
  );
  const genderQueryResults = parseQueryResults(genderResult.response);

  const sex = parseGender(genderQueryResults);

  // Calculate confidence from blocks with actual text
  const blocks =
    idResult.response.IdentityDocuments?.[0]?.IdentityDocumentFields || [];

  // Map to ValueDetection objects with Text field
  const valueDetections = blocks
    .map((field) => field.ValueDetection)
    .filter(
      (v): v is { Text: string; Confidence: number } =>
        v !== undefined && v.Confidence !== undefined && v.Text !== undefined,
    );

  const confidence = calculateConfidence(valueDetections);

  console.log("ID OCR blocks:", blocks);
  console.log("Value detections:", valueDetections);
  console.log("Calculated confidence:", confidence);

  // Additional validation: Check if critical fields have actual content
  const hasCriticalData =
    (idData.firstName && idData.firstName.trim().length > 0) ||
    (idData.lastName && idData.lastName.trim().length > 0) ||
    (idData.dob && idData.dob.trim().length > 0);

  if (!hasCriticalData) {
    datadogLogs.logger.warn(
      "ID OCR returned no critical data (firstName, lastName, or DOB)",
      {
        confidence,
        threshold,
        contextKey,
        consumerId,
        orgId,
        extractedData: idData,
      },
    );

    const errorData: AnalyzeOcrError = {
      code: "LOW_CONFIDENCE",
      message:
        "No critical data extracted from ID (firstName, lastName, or DOB missing)",
      retryable: true,
      confidence,
      mode: "id",
      contextKey,
    };
    const error = new Error(errorData.message);
    Object.assign(error, errorData);
    throw error;
  }

  // Check threshold
  if (!meetsConfidenceThreshold(confidence, threshold)) {
    datadogLogs.logger.warn("ID OCR confidence below threshold", {
      confidence,
      threshold,
      contextKey,
      consumerId,
      orgId,
    });

    const errorData: AnalyzeOcrError = {
      code: "LOW_CONFIDENCE",
      message: `OCR confidence (${(confidence * 100).toFixed(
        1,
      )}%) below threshold (${(threshold * 100).toFixed(1)}%)`,
      retryable: true,
      confidence,
      mode: "id",
      contextKey,
    };
    const error = new Error(errorData.message);
    Object.assign(error, errorData);
    throw error;
  }

  // Log usage
  logOcrUsage({
    documentType: "id",
    pageCount: 1,
    featureTypes: ["ANALYZE_ID", "QUERIES"],
    confidence,
    success: true,
    consumerId,
    orgId,
  });

  // Return data keyed by contextKey (e.g., "ocrId")
  return {
    [contextKey]: {
      success: true,
      confidence,
      firstName: idData.firstName,
      lastName: idData.lastName,
      middleName: idData.middleName,
      dob: idData.dob,
      sex,
      address: idData.address,
      idNumber: idData.idNumber,
      expirationDate: idData.expirationDate,
    },
  };
}

/**
 * Process insurance card OCR
 */
async function processInsuranceOcr(
  imageBase64: string,
  queries: string[],
  threshold: number,
  contextKey: string,
  consumerId?: string,
  orgId?: number,
): Promise<AnalyzeOcrSuccess> {
  // Call AnalyzeDocument endpoint with queries
  const result = await analyzeDocumentImage(imageBase64, queries);

  if (!result.response) {
    const errorData: AnalyzeOcrError = {
      code: "OCR_API_ERROR",
      message: "AnalyzeDocument failed",
      retryable: true,
      mode: "insurance",
      contextKey,
    };
    const error = new Error(errorData.message);
    Object.assign(error, errorData);
    throw error;
  }

  // Parse query results and bounding boxes
  const { results: queryResults, boundingBoxes } =
    parseQueryResultsWithBoundingBoxes(result.response);

  // Calculate confidence
  const blocks = result.response.Blocks || [];
  const queryResultBlocks = blocks.filter(
    (block) => block.BlockType === "QUERY_RESULT",
  );
  const confidence = calculateConfidence(queryResultBlocks);

  console.log(confidence, queryResultBlocks);

  // Check threshold
  if (!meetsConfidenceThreshold(confidence, threshold)) {
    datadogLogs.logger.warn("Insurance OCR confidence below threshold", {
      confidence,
      threshold,
      contextKey,
      consumerId,
      orgId,
      extractedData: queryResults,
    });

    const errorData: AnalyzeOcrError = {
      code: "LOW_CONFIDENCE",
      message: `OCR confidence (${(confidence * 100).toFixed(
        1,
      )}%) below threshold (${(threshold * 100).toFixed(1)}%)`,
      retryable: true,
      confidence,
      mode: "insurance",
      contextKey,
      boundingBoxes,
      queryResults,
    };
    const error = new Error(errorData.message);
    Object.assign(error, errorData);
    throw error;
  }

  // Log usage
  logOcrUsage({
    documentType: "insurance",
    pageCount: 1,
    featureTypes: ["QUERIES"],
    confidence,
    success: true,
    consumerId,
    orgId,
  });

  // Extract normalized expiration date from query results
  const expirationDate = queryResults["What is the expiration date?"] || null;

  // Return data keyed by contextKey (e.g., "ocrPrimaryInsurance" or "ocrSecondaryInsurance")
  return {
    [contextKey]: {
      mode: "insurance" as const,
      contextKey,
      success: true,
      confidence,
      expirationDate,
      boundingBoxes,
      ...queryResults,
    },
  };
}

/**
 * Parse gender from query results
 */
function parseGender(
  queryResults: Record<string, string | null>,
): string | null {
  // Check both gender question variations
  const genderValue =
    queryResults["What is the person's gender?"] ||
    queryResults["What is the person's sex?"];

  if (!genderValue) return null;

  // Normalize
  const text = genderValue.toLowerCase().trim();
  if (text.includes("male") && !text.includes("female")) return "M";
  if (text.includes("female")) return "F";
  if (text === "m") return "M";
  if (text === "f") return "F";

  return null;
}

/**
 * Analyze OCR Actor with Metadata
 */
export const analyzeOcrActor: ActorWithMetadata = {
  impl: analyzeOcrActorImpl,
  metadata: {
    displayName: "analyzeOcr",
    description:
      "Extract information from ID cards or insurance cards using AWS Textract",
    category: "data",
    retryable: true,
    timeout: 30000, // 30 seconds
    errorCodes: [
      "INVALID_INPUT",
      "MISSING_IMAGE",
      "MISSING_QUERIES",
      "OCR_API_ERROR",
      "LOW_CONFIDENCE",
      "NETWORK_ERROR",
      "PARSE_ERROR",
    ],
    inputSchema: AnalyzeOcrInputSchema,
    successSchema: AnalyzeOcrSuccessSchema,
    errorSchema: AnalyzeOcrErrorSchema,
    onSuccess: (result: unknown) => {
      const typedResult = result as AnalyzeOcrSuccess;
      // Return the data object which contains the keyed result
      return typedResult;
    },
    onError: (error: unknown) => {
      const err = error as AnalyzeOcrError;
      return {
        [`${err.contextKey}Error`]: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
          confidence: err.confidence,
        },
        // Return data keyed by contextKey with success: false
        [err.contextKey]: {
          success: false,
          confidence: err.confidence || 0,
        },
      };
    },
  },
};
